import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { take } from "lodash";
import isEmpty from "lodash/isEmpty";
import type { FC } from "react";
import { Link } from "react-router-dom";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../../misc/useLimit";
import type { PublicPackageResponse } from "../../publicPackages/publicPackagesApi";
import { formatDate } from "./formatDate";
import { useECommerceOffersForPackage } from "./useECommerceOffersForPackage";

export interface ECommerceTrip {
  name: string;
  packageId: string;
  participantsNumber: number;
  orderId: string;
  dates: {
    from: string;
    to: string;
  };
}

function deduplicateByDate(offers: ECommerceTrip[]): ECommerceTrip[] {
  const seen = new Set<string>();
  return offers.filter((offer) => {
    const key = `${offer.dates.from}|${offer.dates.to}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

interface Props {
  readonly forPackage: PublicPackageResponse;
}

export const ECommerceTrips: FC<Props> = ({ forPackage }) => {
  const pkgId = forPackage.id.id;
  const { offers: offersFromPackage } = useECommerceOffersForPackage(pkgId);
  const { limit, loadMore } = useLimit(5);
  const offers = deduplicateByDate(offersFromPackage);
  if (isEmpty(offers)) return <></>;

  return (
    <Paper elevation={0} sx={{ p: 4, borderRadius: 1, border: "1px solid rgba(0,0,0,0.08)", mt: 3 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "center",
          mb: 2,
        }}
      >
        <ShoppingCartCheckoutIcon color="primary" fontSize="small" />
        <Typography
          variant="subtitle2"
          sx={{
            textTransform: "uppercase",
            fontWeight: "bold",
            color: "text.secondary",
          }}
        >
          Gotowe wycieczki w sklepie online
        </Typography>
      </Stack>
      <InfiniteScroll
        dataLength={limit < offers.length ? limit : offers.length}
        hasMore={offers.length > limit}
        fetchData={loadMore}
        scrollableTarget={"ecommerce-trips-target"}
        maxHeight={450}
      >
        <Stack
          spacing={2}
          sx={{
            pr: 1,
          }}
        >
          {take(offers, limit).map((trip) => (
            <Box
              key={trip.orderId}
              sx={{ p: 2, borderRadius: 1, bgcolor: "rgba(0,0,0,0.02)", border: "1px solid rgba(0,0,0,0.04)" }}
            >
              <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                    }}
                  >
                    {formatDate(trip.dates.from)} - {formatDate(trip.dates.to)}
                  </Typography>
                </Stack>
              </Stack>
              <Button
                variant="outlined"
                component={Link}
                size="small"
                fullWidth
                sx={{ borderRadius: 1.5, textTransform: "none", py: 0.5 }}
                to={`/e-commerce/${trip.orderId}`}
              >
                Zobacz w e-commerce
              </Button>
            </Box>
          ))}
        </Stack>
      </InfiniteScroll>
    </Paper>
  );
};
